// src/pages/Exercises.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Lesson, UserProgress, User } from "@/api/entities";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { ArrowLeft, CheckCircle2, Dumbbell } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";

const LS_PROGRESS_KEY = "RB_UserProgress";

function safeArray(v) { return Array.isArray(v) ? v : []; }

// ✅ fallback: includi sempre queste lesson.order tra gli esercizi visibili
const EXER_FALLBACK_ORDERS = new Set([6, 9]);

// ✅ checker tollerante: capisce se una lezione HA un esercizio
function hasExercise(lesson) {
  const l = lesson || {};
  return Boolean(
    l.exercise_instructions ||
      l.exercise_question ||
      l.exercise ||               // stringa generica
      l.exercise_text ||          // testo
      l.exercise_html ||          // html
      l.exercise_steps ||         // lista step
      l.has_exercise ||           // boolean
      EXER_FALLBACK_ORDERS.has(Number(l.order)) // forza #6 e #9
  );
}

// Normalizza un record progress in forma {lesson_id, lesson_completed, exercise_completed}
function normalizeProgress(p) {
  if (!p) return null;
  const lesson_id = p.lesson_id ?? p.lessonId ?? p.lesson ?? p.id ?? null;
  const lesson_completed = !!(p.lesson_completed ?? p.completed ?? p.is_completed ?? p.lessonDone);
  const exercise_completed = !!(p.exercise_completed ?? p.exerciseDone ?? p.doneExercise);
  return lesson_id ? { lesson_id: String(lesson_id), lesson_completed, exercise_completed } : null;
}

// Carica progress da localStorage
function loadLocalProgress() {
  try {
    const raw = localStorage.getItem(LS_PROGRESS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return safeArray(arr)
      .map(normalizeProgress)
      .filter(Boolean);
  } catch {
    return [];
  }
}

// Merge locale + remoto (locale vince)
function mergeProgress(localArr, apiArr) {
  const map = new Map();
  for (const p of safeArray(apiArr).map(normalizeProgress).filter(Boolean)) {
    map.set(p.lesson_id, p);
  }
  for (const p of safeArray(localArr).map(normalizeProgress).filter(Boolean)) {
    map.set(p.lesson_id, p); // override
  }
  return Array.from(map.values());
}

export default function Exercises() {
  const [allLessons, setAllLessons] = useState([]);
  const [progressMerged, setProgressMerged] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        // 1) Lezioni ordinate
        const lessons = await Lesson.list("order");
        const ordered = safeArray(lessons).sort(
          (a, b) => (a?.order ?? 0) - (b?.order ?? 0)
        );
        setAllLessons(ordered);

        // 2) Progress locale + remoto
        const localP = loadLocalProgress();

        let apiP = [];
        try {
          const u = await User.me();
          const tries = [
            { created_by: u.email },
            { created_by: u.id || u.uid },
            { user_id: u.id || u.uid || u.email },
          ];
          for (const q of tries) {
            try {
              const res = await UserProgress.filter(q);
              if (Array.isArray(res) && res.length) { apiP = res; break; }
            } catch {}
          }
        } catch {}

        const merged = mergeProgress(localP, apiP);
        setProgressMerged(merged);

      } catch (e) {
        console.error("Errore caricamento esercizi:", e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // ⬇️ NEW: ricarica i progressi quando un esercizio salva (es. lezione 6 emette "rb:checkin-saved")
  useEffect(() => {
    const reload = async () => {
      try {
        const localP = loadLocalProgress();
        let apiP = [];
        try {
          const u = await User.me();
          const tries = [
            { created_by: u.email },
            { created_by: u.id || u.uid },
            { user_id: u.id || u.uid || u.email },
          ];
          for (const q of tries) {
            try {
              const res = await UserProgress.filter(q);
              if (Array.isArray(res) && res.length) { apiP = res; break; }
            } catch {}
          }
        } catch {}
        setProgressMerged(mergeProgress(localP, apiP));
      } catch (e) {
        console.warn("Reload progress fallito:", e);
      }
    };
    const onSaved = () => reload();
    const onFocus = () => reload();

    window.addEventListener("rb:checkin-saved", onSaved);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("rb:checkin-saved", onSaved);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // ✅ Solo lezioni che hanno un esercizio definito (filtro tollerante + fallback #6/#9)
  const exerciseLessons = useMemo(
    () => safeArray(allLessons).filter(hasExercise),
    [allLessons]
  );

  // Lookup veloce per stato
  const progressByLesson = useMemo(() => {
    const m = new Map();
    for (const p of progressMerged) m.set(String(p.lesson_id), p);
    return m;
  }, [progressMerged]);

  const isLessonCompleted = (lessonId) =>
    !!progressByLesson.get(String(lessonId))?.lesson_completed;

  // ✅ Sblocco: esercizio sbloccato se la SUA lezione è completata
  const isExerciseUnlocked = (lesson) => isLessonCompleted(lesson.id);

  const getExerciseStatus = (lesson) => {
    const p = progressByLesson.get(String(lesson.id));
    if (!isExerciseUnlocked(lesson)) return "locked";
    if (p?.exercise_completed) return "completed";
    return "available";
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <Link to={createPageUrl("Dashboard")}>
            <Button variant="outline" className="mb-4 hover:scale-105 transition-transform">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna alla Bacheca
            </Button>
          </Link>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Esercizi <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Potenzianti</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl">
            Pratica gli esercizi per rafforzare le tue competenze e accelerare la tua crescita personale
          </p>
        </motion.div>

        <div className="space-y-4">
          {exerciseLessons.map((lesson, index) => {
            const status = getExerciseStatus(lesson);
            const isAvailable = status !== "locked";
            const statusMap = {
              completed: "Completato",
              locked: "Completa prima la lezione",
              available: "Disponibile",
            };

            return (
              <motion.div
                key={lesson.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card
                  className={`transition-all duration-300 ${
                    status === "completed"
                      ? "bg-gradient-to-r from-green-50 to-green-100 border-green-200 shadow-lg"
                      : isAvailable
                      ? "bg-white hover:shadow-xl hover:scale-[1.02] shadow-lg cursor-pointer"
                      : "bg-gray-50 border-gray-200"
                  }`}
                  onClick={() => {
                    if (isAvailable) {
                      navigate(createPageUrl(`Lesson?id=${lesson.id}`));
                    }
                  }}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div
                          className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                            status === "completed"
                              ? "bg-green-500"
                              : isAvailable
                              ? "bg-gradient-to-r from-purple-500 to-pink-600"
                              : "bg-gray-400"
                          }`}
                        >
                          {status === "completed" ? (
                            <CheckCircle2 className="w-6 h-6 text-white" />
                          ) : (
                            <Dumbbell className="w-6 h-6 text-white" />
                          )}
                        </div>
                        <div>
                          <CardTitle className={`text-xl ${!isAvailable ? "text-gray-500" : "text-gray-900"}`}>
                            Esercizio {lesson.order}: {lesson.title}
                          </CardTitle>
                          <div className="flex items-center gap-3 mt-1">
                            <Badge
                              variant={
                                status === "completed"
                                  ? "default"
                                  : isAvailable
                                  ? "outline"
                                  : "secondary"
                              }
                            >
                              {statusMap[status]}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      {isAvailable && (
                        <Button
                          className={
                            status === "completed"
                              ? "bg-green-600 hover:bg-green-700"
                              : "bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(createPageUrl(`Lesson?id=${lesson.id}`));
                          }}
                        >
                          {status === "completed" ? "Ripeti" : "Inizia"}
                        </Button>
                      )}
                    </div>
                  </CardHeader>

                  {lesson.description && (
                    <CardContent>
                      <p className={`${!isAvailable ? "text-gray-400" : "text-gray-600"}`}>
                        {lesson.description}
                      </p>
                    </CardContent>
                  )}
                </Card>
              </motion.div>
            );
          })}
        </div>

        {exerciseLessons.length === 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12">
            <Dumbbell className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">Nessun esercizio disponibile</h3>
            <p className="text-gray-500">Completa le lezioni per sbloccare gli esercizi pratici.</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
